import { BaseCanvasPack } from "./BaseCanvasPack";

export default class CosmicCanvasPack extends BaseCanvasPack {
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#1a0b2e");
    gradient.addColorStop(1, "#000000");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    if (frameTime !== undefined) {
      this.drawStars(context, width, height, frameTime);
    }
  }

  /**
   * Draws the canvas frame with cosmic theme - neon borders and stars
   */
  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // Neon border
    const borderWidth = 2;
    context.shadowBlur = 10;
    context.shadowColor = "#00FFFF";
    context.strokeStyle = "#00FFFF";
    context.lineWidth = borderWidth;
    context.strokeRect(0.5, 0.5, width - 1, height - 1);
    
    // Reset shadow
    context.shadowBlur = 0;
  }

  /**
   * Draws overlay effects on top of game pieces
   */
  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    if (frameTime !== undefined) {
      // Draw foreground stardust/nebula clouds
      this.drawStardust(context, width, height, frameTime);
    }
  }

  private drawStars(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    context.fillStyle = "rgba(255, 255, 255, 0.8)";
    
    const count = 50;
    
    for (let i = 0; i < count; i++) {
      const seed = i * 123.45;
      const x = (Math.sin(seed) * 10000) % width;
      const y = (Math.cos(seed) * 10000) % height;
      const size = (Math.sin(seed * 2) + 2) * 0.5;
      
      // Twinkle effect
      const twinkle = Math.sin(frameTime * 0.005 + seed * 10) * 0.5 + 0.5;
      context.globalAlpha = twinkle * 0.8;
      
      context.beginPath();
      context.arc(Math.abs(x), Math.abs(y), size, 0, Math.PI * 2);
      context.fill();
    }
    
    // Shooting star (occasional)
    const shootingStarTime = frameTime % 5000;
    if (shootingStarTime < 500) {
      const progress = shootingStarTime / 500;
      const sx = width * 0.8 - progress * width * 0.5;
      const sy = height * 0.1 + progress * height * 0.5;
      
      context.globalAlpha = 1 - progress;
      context.strokeStyle = "#FFFFFF";
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(sx, sy);
      context.lineTo(sx + 20, sy - 20);
      context.stroke();
    }

    context.restore();
  }

  private drawStardust(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    
    // Drifting nebula clouds (simulated with large soft circles)
    const cloudCount = 5;
    for (let i = 0; i < cloudCount; i++) {
      const seed = i * 789.12;
      const speed = 0.01;
      const x = (Math.sin(seed) * 1000 + frameTime * speed) % (width + 200) - 100;
      const y = (Math.cos(seed) * 1000 + frameTime * speed * 0.5) % (height + 200) - 100;
      
      const gradient = context.createRadialGradient(x, y, 0, x, y, 100);
      gradient.addColorStop(0, `rgba(${100 + i * 30}, 0, 255, 0.1)`);
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      
      context.fillStyle = gradient;
      context.beginPath();
      context.arc(x, y, 100, 0, Math.PI * 2);
      context.fill();
    }

    context.restore();
  }

  getBorderStyle(): string {
    return "2px solid #00FFFF";
  }

  getBoxShadow(frameTime?: number): string {
    let glow = 0.5;
    if (frameTime !== undefined) {
      glow = 0.5 + 0.2 * Math.sin(frameTime * 0.002);
    }
    return `0 0 20px rgba(0, 255, 255, ${glow}), 0 0 40px rgba(138, 43, 226, ${glow * 0.5})`;
  }

  getBackdropFilter(): string {
    return "blur(4px)";
  }
}

